import React from "react";
import { Box, Chip, Typography, useMediaQuery, useTheme } from "@mui/material";
import Text from "@ds/components/Text";
import Link from "next/link";
import color from "@definitions/mui/color";
import sanitizeHtml from "sanitize-html";

export interface WideCardProps {
  title: string;
  description: string;
  date: string;
  image: string;
  tag?: string;
  linkTo?: string;
  subtitle?: string;
  type?: "default" | "border";
  descriptionEllipsisRow?: number;
  isGlobalSearch?: boolean;
  isNoTruncateText?: boolean;
}

export const WideCard: React.FC<WideCardProps> = ({
  date,
  description,
  title,
  image,
  tag,
  linkTo,
  subtitle,
  type = "default",
  isNoTruncateText = false,
  descriptionEllipsisRow,
  isGlobalSearch,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const contentHtmlSanitize = sanitizeHtml(description, {
    allowedTags: ["span"],
    allowedAttributes: {},
    transformTags: {
      br: "span",
    },
  });

  const titleContainer = isNoTruncateText ? (
    <Typography
      variant={"body1"}
      fontWeight="bold"
      fontSize="18px"
      mb={isMobile ? "8px" : "6px"}
      fontFamily={isMobile ? "Nunito" : "Open Sans"}
      color={color.general.main}
      sx={{
        WebkitBoxOrient: "vertical",
        wordBreak: "break-word",
        lineHeight: isMobile ? "20px" : "22px",
      }}
    >
      {title}
    </Typography>
  ) : (
    <Typography
      variant="body1"
      fontWeight="bold"
      mb={isMobile ? "8px" : "6px"}
      fontFamily={isMobile ? "Nunito" : "Open Sans"}
      color={color.general.main}
      sx={{
        fontSize: isMobile ? "14px" : "16px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: isGlobalSearch ? 2 : 1,
        WebkitBoxOrient: "vertical",
        wordBreak: "break-word",
        lineHeight: isMobile ? "20px" : "22px",
      }}
    >
      {title}
    </Typography>
  );

  const dateContainer = (
    <Text component="div" variant="caption1" color="inactive-secondary">
      {date}
    </Text>
  );

  const descriptionContent = (
    <Box
      color={color.general.main}
      fontSize="14px"
      fontWeight="400"
      overflow="hidden"
      textOverflow="ellipsis"
      display="-webkit-box"
      fontFamily="Nunito"
      mb={isMobile ? "8px" : "6px"}
      sx={{
        WebkitLineClamp: descriptionEllipsisRow ? descriptionEllipsisRow : 2,
        WebkitBoxOrient: "vertical",
      }}
      dangerouslySetInnerHTML={{ __html: contentHtmlSanitize }}
    />
  );

  const contentCondition = (
    <>
      {!!subtitle && (
        <Text
          component="p"
          variant="body2"
          color="blue-secondary"
          sx={{
            fontWeight: isMobile ? "400" : "600",
            marginBottom: isMobile ? "8px" : "6px",
          }}
        >
          {subtitle}
        </Text>
      )}
      {titleContainer}
      {isMobile ? <>{isGlobalSearch ? null : descriptionContent}</> : descriptionContent}
      {!!tag && (
        <Chip
          label={tag}
          sx={{
            backgroundColor: "#293780",
            color: "#FFF",
            fontSize: "14px",
            marginBottom: "8px",
            padding: "6px 12px",
          }}
        />
      )}
    </>
  );

  const content = (
    <Box
      display="flex"
      mb="24px"
      sx={{
        cursor: linkTo ? "pointer" : undefined,
        border: type === "border" ? `1px solid ${color.grey.light}` : "none",
        borderRadius: "8px",
        transition: "all 200ms",
        "&:hover": {
          backgroundColor: isGlobalSearch ? color.light : color.inactive.primary,
          ...(isGlobalSearch && {
            boxShadow: "0px 6px 12px rgba(46, 67, 77, 0.1)",
            ".MuiTypography-body1": {
              color: color.primary.main,
            },
          }),
        },
      }}
    >
      <Box
        width={isMobile ? "120px" : "140px"}
        height={isMobile ? "120px" : "140px"}
        bgcolor="#EDF0F2"
        borderRadius={type === "border" ? "8px 0 0 8px" : "8px"}
        flexShrink={0}
        marginRight="12px"
        sx={{ background: `#eee url("${image}") center/cover no-repeat` }}
      />
      {description ? (
        <Box
          p="8px"
          pl={0}
          display={descriptionEllipsisRow === 1 ? "grid" : undefined}
          sx={{
            ...(isMobile && {
              display: "inline-flex",
              alignItems: "flex-start",
              flexDirection: "column",
              justifyContent: "space-between",
            }),
          }}
        >
          {isMobile ? <div>{contentCondition}</div> : contentCondition}
          {dateContainer}
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" flexDirection="column">
          {titleContainer}
          {dateContainer}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {linkTo ? (
        <Link href={linkTo}>
          <a style={{ width: "100%" }}>{content}</a>
        </Link>
      ) : (
        content
      )}
    </>
  );
};
